import React, { useCallback, useEffect, useState } from 'react';
import { Scissors } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  DataListTable,
  ErrorState,
  LoadingState,
  PaginationControls,
  SectionCard,
  formatNumber,
} from '../../components/superadmin/SuperAdminUI';

const DEFAULT_PAGINATION = { page: 1, pageSize: 20, total: 0, totalPages: 0 };

const ShopBarbers = () => {
  const { shop, shopId } = useOutletContext();
  const { getShopBarbers } = useSuperadminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [barbers, setBarbers] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const loadBarbers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await getShopBarbers(shopId, {
        page,
        pageSize: pagination.pageSize,
      });

      if (!response?.ok) {
        throw new Error('Failed to load barbers');
      }

      setBarbers(response.data || []);
      setPagination(response.pagination || DEFAULT_PAGINATION);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load barbers');
    } finally {
      setLoading(false);
    }
  }, [getShopBarbers, pagination.pageSize, shopId]);

  useEffect(() => {
    loadBarbers(1);
  }, [loadBarbers]);

  return (
    <div className="space-y-6">
      {error ? <ErrorState error={error} onRetry={() => loadBarbers(pagination.page)} /> : null}

      <SectionCard
        title="Barbers"
        subtitle={`Current barber roster for ${shop?.name || `Shop #${shopId}`}`}
        icon={<Scissors className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        <div className="space-y-4">
          {loading ? (
            <LoadingState lines={6} />
          ) : (
            <DataListTable
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'email', label: 'Email' },
                { key: 'bookings', label: 'Bookings', render: (row) => formatNumber(row.bookings?.total) },
                { key: 'scans', label: 'AI scans', render: (row) => formatNumber(row.scans?.total) },
              ]}
              rows={barbers}
              emptyTitle="No barbers found"
              emptyDescription="No barber memberships were returned for this shop."
            />
          )}

          <PaginationControls pagination={pagination} onPageChange={loadBarbers} loading={loading} />
        </div>
      </SectionCard>
    </div>
  );
};

export default ShopBarbers;
